import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/mac-os-ui.css';

const SECTIONS = [
  { id: 'audits', label: 'Audit' },
  { id: 'bugbounty', label: 'Bug Bounty' },
  { id: 'contests', label: 'Contest' },
  { id: 'contributions', label: 'Contribution' },
  { id: 'contact', label: 'Contact' },
];

const MacMenuBar = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const now = new Date();
    setCurrentTime(now);

    // Only update every 60s since we only display HH:MM
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    let minuteTimer;
    const startMinuteTimer = () => {
      minuteTimer = setInterval(() => setCurrentTime(new Date()), 60000);
    };

    const initialTimeout = setTimeout(() => {
      setCurrentTime(new Date());
      startMinuteTimer();
    }, msUntilNextMinute);

    // Pause when tab is hidden, sync when visible again
    const handleVisibility = () => {
      if (!document.hidden) {
        setCurrentTime(new Date());
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(minuteTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // IntersectionObserver for active section highlighting
  useEffect(() => {
    const sectionIds = SECTIONS.map(s => s.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-36px 0px -60% 0px', threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  // Close menu on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && menuOpen) {
        closeMenu();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, closeMenu]);

  const formatTime = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours}:${minutes < 10 ? '0' + minutes : minutes}`;
  };

  return (
    <nav className="mac-menu-bar" aria-label="Main navigation">
      <div className="mac-menu-item">
        <button
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            closeMenu();
          }}
          style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
          aria-label="Scroll to top"
        >
          <img
            src="images/merlinboii-pixel.png"
            style={{ verticalAlign: 'middle', width: '25px', height: '25px' }}
            alt="Logo"
            className="mac-apple-logo"
          />
        </button>
      </div>

      <button
        className={`hamburger${menuOpen ? ' active' : ''}`}
        onClick={toggleMenu}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <div className={`mac-menu-links${menuOpen ? ' open' : ''}`}>
        {SECTIONS.map(({ id, label }) => (
          <a
            key={id}
            className={`mac-menu-item${activeSection === id ? ' active' : ''}`}
            href={`#${id}`}
            onClick={closeMenu}
          >
            {label}
          </a>
        ))}
      </div>

      <div className="mac-clock">
        {formatTime(currentTime)}
      </div>
    </nav>
  );
};

export default MacMenuBar;
